import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from '../../utils/classNames';

const LINE_CLAMP_CLASS = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3'
};

export const Tooltip = ({
  content,
  children,
  className,
  position = 'bottom',
  multiline = false,
  lines = 2,
  align = 'center'
}) => {
  const wrapperRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const hasContent = Boolean(content);

  const lineClampClass = LINE_CLAMP_CLASS[lines] || LINE_CLAMP_CLASS[2];
  const shouldUsePortal = typeof document !== 'undefined';

  const tooltipClassName = useMemo(
    () =>
      classNames(
        'pointer-events-none fixed z-[9999] w-max max-w-[12.5rem] rounded-lg bg-slate-950 px-2.5 py-1.5 text-[10px] font-medium text-white opacity-0 shadow-[0_8px_24px_rgba(15,23,42,0.22)] transition duration-150 dark:bg-white dark:text-slate-950',
        multiline
          ? classNames('text-start leading-snug', lineClampClass)
          : 'whitespace-nowrap rounded-full py-1 font-semibold'
      ),
    [lineClampClass, multiline]
  );

  const updateTooltipPosition = useCallback(() => {
    if (!wrapperRef.current) {
      return;
    }

    const triggerRect = wrapperRef.current.getBoundingClientRect();
    const offset = 6;
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const tooltipTop =
      position === 'top' ? triggerRect.top - offset : triggerRect.bottom + offset;
    let tooltipLeft = triggerCenterX;

    if (align === 'start') {
      tooltipLeft = triggerRect.left;
    }

    if (align === 'end') {
      tooltipLeft = triggerRect.right;
    }

    setCoords({ top: tooltipTop, left: tooltipLeft });
  }, [align, position]);

  useEffect(() => {
    if (!hasContent || !isVisible) {
      return undefined;
    }

    updateTooltipPosition();
    window.addEventListener('scroll', updateTooltipPosition, true);
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      window.removeEventListener('scroll', updateTooltipPosition, true);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [hasContent, isVisible, updateTooltipPosition]);

  return (
    <div
      ref={wrapperRef}
      className={classNames('relative inline-flex', className)}
      onMouseEnter={() => hasContent && setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => hasContent && setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {children}
      {hasContent && shouldUsePortal
        ? createPortal(
            <div
              className={classNames(tooltipClassName, isVisible ? 'opacity-100' : 'opacity-0')}
              style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                transform: classNames(
                  position === 'top' ? '-translate-y-full' : 'translate-y-0',
                  align === 'center' ? '-translate-x-1/2' : align === 'end' ? '-translate-x-full' : ''
                )
              }}
              role="tooltip"
              aria-hidden={!isVisible}
            >
              {content}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};
